import { useState } from "react";
import { KeyRound, LoaderCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionTitle } from "@/components/ui/section-title";
import { UsuarioService } from "@/services";
import { Usuario } from "@/types/usuario";

type SenhaSectionProps = {
  usuario: Usuario;
  // Na própria conta não faz sentido pedir troca no próximo acesso
  propria: boolean;
}

// Fica dentro do formulário do usuário: sem <form> aqui, os botões são type="button"
export const SenhaSection = ({ usuario, propria }: SenhaSectionProps) => {
  const [aberto, setAberto] = useState(false);
  const [senha, setSenha] = useState('');
  const [temporaria, setTemporaria] = useState(!propria);
  const [salvando, setSalvando] = useState(false);

  const abrir = () => {
    setSenha('');
    setTemporaria(!propria);
    setAberto(true);
  }

  const alterar = () => {
    setSalvando(true);
    UsuarioService.alterarSenha(usuario.id!, senha, temporaria)
      .then(() => {
        toast.success('Senha alterada.', temporaria ? { description: 'No próximo acesso será pedida uma nova senha.' } : undefined);
        setAberto(false);
      })
      .catch(() => {})
      .finally(() => setSalvando(false));
  }

  const enviarEmail = () => {
    const idLoading = toast.loading(`Enviando e-mail para ${usuario.email}`);
    UsuarioService.solicitarNovaSenha(usuario.id!)
      .then(() => toast.info(`Enviamos para ${usuario.email} o link para definir uma nova senha.`))
      .catch(() => {})
      .finally(() => toast.dismiss(idLoading));
  }

  return (
    <>
    <SectionTitle icon={<KeyRound />} cor="bg-amber-500/10 text-amber-600 dark:text-amber-400"
      description="Defina uma nova senha agora ou envie um e-mail para a pessoa escolher a senha.">
      Senha
    </SectionTitle>
    <div className="flex flex-wrap gap-2">
      <Button type="button" variant="outline" onClick={abrir}><KeyRound className="mr-2 h-4 w-4" /> Alterar senha</Button>
      <Button type="button" variant="outline" onClick={enviarEmail}><Mail className="mr-2 h-4 w-4" /> Enviar e-mail para troca de senha</Button>
    </div>

    <Dialog open={aberto} onOpenChange={setAberto}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Alterar senha</DialogTitle>
          <DialogDescription>Nova senha de {usuario.nome || usuario.email}. Mínimo de 8 caracteres, diferente do e-mail.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="nova-senha">Nova senha</Label>
            <Input id="nova-senha" type="password" autoComplete="new-password" value={senha}
              onChange={e => setSenha(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (senha.length >= 8) alterar(); } }} />
          </div>
          {!propria && (
            <label className="flex items-center gap-2.5 text-sm">
              <Checkbox checked={temporaria} onCheckedChange={v => setTemporaria(v === true)} />
              Pedir para trocar a senha no próximo acesso
            </label>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setAberto(false)}>Cancelar</Button>
          <Button type="button" onClick={alterar} disabled={salvando || senha.length < 8}>
            {salvando && <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />}
            Alterar senha
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
